// Generates a unique-enough client-side id (e.g. for `clientMessageId`-based optimistic-send
// deduplication in the chat feature). Prefers `crypto.randomUUID()`, but that API is only exposed
// in a "secure context" (HTTPS or `localhost`) -- accessing the app over a plain-HTTP LAN IP (e.g.
// `192.168.x.x:4200`, useful for testing from a phone or another machine) leaves `crypto.randomUUID`
// undefined and throws. Falls back to a `Math.random()` + timestamp token in that case: it does not
// need to be cryptographically secure, only unique enough to dedupe the SignalR echo of a message
// this same client just sent.
export function generateClientMessageId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

// Same secure-context trap as above, but this id travels to the backend as `X-Correlation-Id` and
// is matched against the ids ProVet diagnostics reports back, so it must keep the UUID v4 SHAPE
// rather than degrade to an arbitrary token. `crypto.getRandomValues` IS available outside a secure
// context, so the fallback builds the v4 by hand from it; the last resort covers an environment
// with no WebCrypto at all (jsdom without the polyfill), where a collision is harmless because the
// id is only ever used to group requests within one session.
export function generateCorrelationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  const random = (length: number): string =>
    Array.from({ length }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return `${random(8)}-${random(4)}-4${random(3)}-a${random(3)}-${random(12)}`;
}
